import { jsPDF } from 'jspdf';
import { fetchBusinessProfile } from './businessProfile';
import { money } from './format';
import {
  PDF_BRAND,
  drawCard,
  drawLabelValue,
  drawPdfHeader,
  drawSectionTitle,
  drawStatusPill,
  ensureSpace,
  finalizePdfFooters,
  pageMetrics,
} from './pdfTheme';
import {
  MARKET_CONDITION_LABELS,
  RESALE_SECTION_KEYS,
  RESALE_SECTION_LABELS,
} from './resaleScore';

function scoreAccent(score) {
  if (score >= 85) return PDF_BRAND.good;
  if (score >= 70) return PDF_BRAND.accent;
  if (score >= 55) return PDF_BRAND.fair;
  return PDF_BRAND.critical;
}

function drawScoreBadge(doc, score, marketLabel, x, y) {
  const size = 88;
  drawCard(doc, x, y, size, size);
  const color = scoreAccent(Number(score) || 0);
  doc.setDrawColor(...color);
  doc.setLineWidth(4);
  doc.circle(x + size / 2, y + size / 2 - 4, 28, 'S');
  doc.setTextColor(...PDF_BRAND.ink);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(28);
  doc.text(String(score ?? '—'), x + size / 2, y + size / 2, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...PDF_BRAND.muted);
  doc.text('OVERALL', x + size / 2, y + size - 12, { align: 'center' });

  drawCard(doc, x + size + 12, y + 18, 150, 52);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...PDF_BRAND.muted);
  doc.text('MARKET CONDITION', x + size + 24, y + 38);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(...PDF_BRAND.ink);
  doc.text(marketLabel || '—', x + size + 24, y + 56);
}

function drawTableHeader(doc, x, y, width) {
  doc.setFillColor(...PDF_BRAND.ink);
  doc.roundedRect(x, y, width, 24, 6, 6, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text('Section', x + 10, y + 15);
  doc.text('Status', x + width * 0.55, y + 15);
  doc.text('Score', x + width - 10, y + 15, { align: 'right' });
  return y + 30;
}

function drawSectionRow(doc, label, section, x, y, width, zebra) {
  const rowH = section?.notes ? 42 : 28;
  doc.setFillColor(...(zebra ? PDF_BRAND.soft : PDF_BRAND.panel));
  doc.roundedRect(x, y, width, rowH, 4, 4, 'F');
  doc.setTextColor(...PDF_BRAND.ink);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9.5);
  doc.text(label, x + 10, y + 16, { maxWidth: width * 0.48 });
  drawStatusPill(doc, section?.status || 'good', x + width * 0.55, y + 14);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...PDF_BRAND.ink);
  doc.text(String(section?.score ?? '—'), x + width - 10, y + 16, { align: 'right' });
  if (section?.notes) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(...PDF_BRAND.muted);
    doc.text(section.notes, x + 10, y + 32, { maxWidth: width - 20 });
  }
  return y + rowH + 4;
}

export function resaleFileName(report) {
  const short = `${report?.report_number || report?.id || 'report'}`.replace(/\s+/g, '-');
  return `resale-inspection-${short}.pdf`;
}

export async function downloadResalePdf(report) {
  const business = await fetchBusinessProfile();
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const margin = 40;
  const { contentWidth, pageHeight } = pageMetrics(doc, margin);
  let y = await drawPdfHeader(doc, business, {
    margin,
    subtitle: 'Certified pre-purchase inspection',
    badge: report?.report_number || 'Inspection',
  });

  const vehicle = [report.brand, report.model, report.year].filter(Boolean).join(' ');
  const finalized = report.finalized_at
    ? new Date(report.finalized_at).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : null;

  doc.setTextColor(...PDF_BRAND.ink);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.text(vehicle || report.report_number || 'Vehicle inspection', margin, y);
  y += 22;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...PDF_BRAND.muted);
  const meta = [
    report.report_number,
    finalized,
    report.inspector_name ? `Inspector: ${report.inspector_name}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
  if (meta) doc.text(meta, margin, y);
  y += 24;

  drawScoreBadge(
    doc,
    report.overall_score,
    MARKET_CONDITION_LABELS[report.market_condition] || report.market_condition,
    margin,
    y,
  );
  y += 104;

  const colGap = 12;
  const colWidth = (contentWidth - colGap) / 2;
  drawCard(doc, margin, y, colWidth, 108);
  let cardY = y + 16;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...PDF_BRAND.ink);
  doc.text('Vehicle', margin + 12, cardY);
  cardY += 18;
  cardY = drawLabelValue(doc, 'Plate', report.plate_number, margin + 12, cardY, colWidth - 24);
  cardY = drawLabelValue(doc, 'VIN', report.vin, margin + 12, cardY, colWidth - 24);
  cardY = drawLabelValue(
    doc,
    'Mileage',
    report.mileage_km ? `${Number(report.mileage_km).toLocaleString()} km` : null,
    margin + 12,
    cardY,
    colWidth - 24,
  );

  const sellerX = margin + colWidth + colGap;
  drawCard(doc, sellerX, y, colWidth, 108);
  cardY = y + 16;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('Seller', sellerX + 12, cardY);
  cardY += 18;
  cardY = drawLabelValue(doc, 'Name', report.seller_name, sellerX + 12, cardY, colWidth - 24);
  cardY = drawLabelValue(doc, 'Phone', report.seller_phone, sellerX + 12, cardY, colWidth - 24);
  drawLabelValue(doc, 'City', report.seller_city, sellerX + 12, cardY, colWidth - 24);

  y += 124;

  y = drawSectionTitle(doc, 'Inspection breakdown', margin, y, contentWidth);
  y = drawTableHeader(doc, margin, y, contentWidth);
  RESALE_SECTION_KEYS.forEach((key, index) => {
    y = ensureSpace(doc, y, 48, margin, () => margin + 20);
    y = drawSectionRow(
      doc,
      RESALE_SECTION_LABELS[key],
      report.sections?.[key] || {},
      margin,
      y,
      contentWidth,
      index % 2 === 0,
    );
  });

  if ((report.immediate_repairs || []).length) {
    y += 12;
    y = ensureSpace(doc, y, 60, margin, () => margin + 20);
    y = drawSectionTitle(doc, 'Immediate repairs', margin, y, contentWidth);
    for (const row of report.immediate_repairs) {
      y = ensureSpace(doc, y, 34, margin, () => margin + 20);
      drawCard(doc, margin, y, contentWidth, 30);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9.5);
      doc.setTextColor(...PDF_BRAND.ink);
      doc.text(row.title || '—', margin + 12, y + 18);
      const cost =
        row.cost_min_egp != null || row.cost_max_egp != null
          ? `${money(row.cost_min_egp ?? '—')} – ${money(row.cost_max_egp ?? '—')}`
          : '';
      if (cost) {
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...PDF_BRAND.muted);
        doc.text(cost, margin + contentWidth - 12, y + 18, { align: 'right' });
      }
      y += 36;
    }
  }

  if ((report.expected_maintenance || []).length) {
    y += 8;
    y = ensureSpace(doc, y, 60, margin, () => margin + 20);
    y = drawSectionTitle(doc, 'Expected maintenance', margin, y, contentWidth);
    for (const row of report.expected_maintenance) {
      y = ensureSpace(doc, y, 34, margin, () => margin + 20);
      drawCard(doc, margin, y, contentWidth, 30);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9.5);
      doc.text(row.item || '—', margin + 12, y + 18);
      const detail = [
        row.horizon_months ? `${row.horizon_months} months` : null,
        row.cost_egp != null ? money(row.cost_egp) : null,
      ]
        .filter(Boolean)
        .join(' · ');
      if (detail) {
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...PDF_BRAND.muted);
        doc.text(detail, margin + contentWidth - 12, y + 18, { align: 'right' });
      }
      y += 36;
    }
  }

  if (report.summary_notes) {
    y += 12;
    y = ensureSpace(doc, y, 80, margin, () => margin + 20);
    y = drawSectionTitle(doc, 'Inspector summary', margin, y, contentWidth);
    drawCard(doc, margin, y, contentWidth, 64);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    doc.setTextColor(...PDF_BRAND.muted);
    const wrapped = doc.splitTextToSize(report.summary_notes, contentWidth - 24);
    doc.text(wrapped, margin + 12, y + 18);
    y += 76;
  }

  if (report.verification_code) {
    y = ensureSpace(doc, y, 70, margin, () => margin + 20);
    drawCard(doc, margin, y, contentWidth, 56);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(...PDF_BRAND.muted);
    doc.text('VERIFICATION CODE', margin + 12, y + 18);
    doc.setFont('courier', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(...PDF_BRAND.ink);
    doc.text(report.verification_code, margin + 12, y + 38);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...PDF_BRAND.faint);
    doc.text(
      'Buyers can verify this report using the published share link.',
      margin + 180,
      y + 34,
      { maxWidth: contentWidth - 200 },
    );
    y += 68;
  }

  if (y < pageHeight - 120) {
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(8);
    doc.setTextColor(...PDF_BRAND.faint);
    doc.text(
      'This inspection reflects vehicle condition at the time of inspection only. It is not a warranty or ownership guarantee.',
      margin,
      pageHeight - 52,
      { maxWidth: contentWidth },
    );
  }

  finalizePdfFooters(doc, business, margin);
  doc.save(resaleFileName(report));
}
